import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, slugify, getLatestPeriod, fetchAllPaginated, fetchLatestFortnightBreakdown, formatFortnightRange } from '../utils';
import { generateDistrictsListPDF } from '../shareExport';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';

const ShareIcon = ({ size = 16, color = '#0F2238' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const DistrictsBrowsePage = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateFilter = searchParams.get('state') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);
  const [allStates, setAllStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [fortnight, setFortnight] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await getLatestPeriod();
        const [{ data: districtRows, error: dErr }, fortnightBreakdown, millRows] = await Promise.all([
          supabase
            .from('mi_v_district_monthly')
            .select('canonical_state, canonical_district, total_allocated_mt, mills_active, mills_reporting')
            .eq('period_month', latest)
            .order('total_allocated_mt', { ascending: false }),
          fetchLatestFortnightBreakdown(),
          fetchAllPaginated(() =>
            supabase
              .from('mi_v_release_orders_canonical')
              .select('mill_id, canonical_state, canonical_district')
              .eq('period_month', latest)
              .not('canonical_mill_name', 'eq', '')
          ),
        ]);
        if (dErr) throw dErr;
        if (!isMounted) return;
        setMonth(latest);
        setFortnight(fortnightBreakdown);
        if (fortnightBreakdown) {
          const byDistrict = new Map();
          millRows.forEach((r) => {
            const key = `${r.canonical_state}||${r.canonical_district}`;
            const qty = fortnightBreakdown.byMill.get(r.mill_id) || 0;
            byDistrict.set(key, (byDistrict.get(key) || 0) + qty);
          });
          const withFortnight = (districtRows || []).map((d) => ({ ...d, fortnightMt: byDistrict.get(`${d.canonical_state}||${d.canonical_district}`) || 0 }));
          withFortnight.sort((a, b) => b.fortnightMt - a.fortnightMt);
          setDistricts(withFortnight);
        } else {
          setDistricts(districtRows || []);
        }
        setAllStates([...new Set((districtRows || []).map((r) => r.canonical_state))].sort());
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const filtered = stateFilter
    ? districts.filter((d) => slugify(d.canonical_state) === stateFilter)
    : districts;

  const handleStateChange = (e) => {
    const val = e.target.value;
    if (val) setSearchParams({ state: val });
    else setSearchParams({});
  };

  const handleSharePDF = async () => {
    setSharing(true);
    const stateName = stateFilter ? allStates.find((s) => slugify(s) === stateFilter) : null;
    await generateDistrictsListPDF({ districts: filtered, month, stateName, fortnight, action: 'share' });
    setSharing(false);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>All Districts — Sugar Quota Data | SugarHathi Market Intelligence</title>
        <meta name="description" content="District-wise sugar quota release-order data across India. Live monthly allocation figures for every district, verified by SugarHathi." />
      </Helmet>
      <MIHeader subtitle={t('marketIntelligence.allDistricts', 'All districts')} />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-black text-[var(--text-primary)]">{t('marketIntelligence.allDistrictsCount', 'All districts ({{count}})', { count: filtered.length })}</h1>
          <button onClick={handleSharePDF} disabled={sharing || loading || filtered.length === 0}
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[var(--radius-input)] disabled:opacity-50"
            style={{ background: '#F59E0B', color: '#0F2238' }}>
            <ShareIcon size={14} color="#0F2238" /> {sharing ? t('marketIntelligence.preparing', 'Preparing...') : t('marketIntelligence.sharePdf', 'Share PDF')}
          </button>
        </div>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          {fortnight
            ? `${formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel} allocation`
            : `${t('marketIntelligence.completeMonthNote', 'Last complete month')} — ${fmtMonth(month)}`}
        </p>

        <div className="mb-6">
          <label className="text-xs text-[var(--text-muted)] block mb-1.5">{t('marketIntelligence.filterByState', 'Filter by state')}</label>
          <select
            value={stateFilter}
            onChange={handleStateChange}
            className="w-full bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] rounded-[var(--radius-input)] px-3 py-2.5 text-sm font-medium"
          >
            <option value="" style={{ background: 'var(--bg-primary)' }}>{t('marketplace.allStates', 'All States')}</option>
            {allStates.map((s) => (
              <option key={s} value={slugify(s)} style={{ background: 'var(--bg-primary)' }}>{localizedStateName(s, language)}</option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="Loader" size={32} className="animate-spin mb-3" />
            <p>{t('common.loading', 'Loading...')}</p>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="AlertTriangle" size={32} className="mb-3 text-[var(--error)]" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-2">
            {filtered.map((d, i) => (
              <div key={i} className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-card)] p-4 flex items-center justify-between">
                <button
                  onClick={() => navigate(`/market-intelligence/district/${slugify(d.canonical_district)}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <p className="text-sm font-semibold text-[var(--text-primary)]">{localizedDistrictName(d.canonical_district, d.canonical_state, language)}</p>
                  <p className="text-xs text-[var(--text-muted)]">{localizedStateName(d.canonical_state, language)} &middot; {t('marketIntelligence.millsActiveOfReporting', '{{active}} of {{reporting}} active', { active: d.mills_active, reporting: d.mills_reporting })}</p>
                </button>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="text-sm font-bold text-[var(--text-primary)]">{fmtNum(fortnight ? d.fortnightMt : d.total_allocated_mt)} MT</p>
                  <button
                    onClick={() => navigate(`/market-intelligence/factories?district=${slugify(d.canonical_district)}`)}
                    className="text-xs font-medium px-2.5 py-1.5 rounded-[var(--radius-input)]"
                    style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--primary)' }}
                  >
                    {t('marketIntelligence.factoriesButton', 'Factories')}
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-sm text-[var(--text-muted)]">{t('marketIntelligence.noDistrictsMatchFilter', 'No districts match this filter.')}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default DistrictsBrowsePage;

